import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import React, {useEffect, useEffectEvent, useImperativeHandle, useRef} from 'react'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import {useManualRender} from '../../../../hooks/use-manual-render'
import {isLineInViewport, useIsFirefox, useShouldUseInert} from '../../../../utilities/lines'
import SelectAllShortcutButton from '../../../../utilities/SelectAllShortcutButton'
import {CodeFoldingEllipsisOverlay} from './CodeFoldingEllipsisOverlay'
import {type CodeLinesProps, getHorizontalScrollOffset, handleBlobScrollSync} from './CodeLines'
import codeLinesStyles from './CodeLines.module.css'
import {HighlightedLinesOverlay} from './HighlightedLinesOverlay'
import {HighlightedSymbolsOverlay} from './HighlightedSymbolsOverlay'
import {LineNumberNoVirtualziation} from './LineNumberNoVirtualization'
import {StickyLineObserverOverlay} from './StickyLineObserverOverlay'
import {SyntaxHighlightedOverlay} from './SyntaxHighlightedLine'

export const CodeLinesNoVirtualization = React.memo(CodeLinesNoVirtualizationUnmemoized)
export const numberOfLinesPerContentVisibility = 60
export const noVirtualizationThreshold = 3500

function CodeLinesNoVirtualizationUnmemoized({
  linesData,
  onLineNumberClick,
  codeSections,
  nonTruncatedLinesData,
  codeLineToSectionMap,
  onLineStickOrUnstick,
  sharedLineObserver,
  colorizedLines,
  tabSize,
  contentWidth,
  cursorContainerRef,
  onCollapseToggle,
  textAreaRef,
  copilotAccessAllowed,
  ref,
}: CodeLinesProps) {
  const parentRef = useRef<HTMLTableElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const horizontalScrollBarRef = useRef<HTMLDivElement>(null)
  const shouldUseCursor = useIsCursorEnabled()
  const reRender = useManualRender()
  // Prevent loops and jumping when scrolling
  const setNextTextAreaScrollRef = useRef(true)
  const setNextScrollBarScrollRef = useRef(true)
  const setNextScrollContainerScrollRef = useRef(true)
  const lineHeight = useCurrentLineHeight('react-line-numbers')

  //we want to wrap every 60 lines in a data-visibility wrapper, so we need
  //to make this array to map over down below
  const lineArrayMapRef = useRef<number[]>(null)
  if (lineArrayMapRef.current == null) {
    lineArrayMapRef.current = [...Array(Math.floor(linesData.length / numberOfLinesPerContentVisibility) + 1).keys()]
  }

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (isLineInViewport(0)) return
      window.scrollTo({left: 0, top: 0})
    },
    scrollToLine: (lineNumber, column) => {
      const container = parentRef.current
      if (!container) {
        return
      }

      setTimeout(
        () =>
          window.scrollTo({
            left: getHorizontalScrollOffset(container, lineNumber, column),
            top: lineHeight * lineNumber,
          }),
        0,
      )
    },
  }))

  useEffect(() => {
    //need to set the on scroll here because it relies on having access to parentRef to sync up the scrolls
    if (textAreaRef && textAreaRef.current) {
      textAreaRef.current.onscroll = () => {
        if (scrollContainerRef.current && textAreaRef?.current) {
          if (!setNextScrollContainerScrollRef.current) {
            setNextScrollContainerScrollRef.current = true
            return
          }

          if (textAreaRef.current.scrollLeft === scrollContainerRef.current.scrollLeft) return

          // Goes in a loop textArea -> contianer -> scrollBar -> textArea , so we need to prevent the next scroll on the textArea
          setNextTextAreaScrollRef.current = !(
            setNextScrollBarScrollRef.current && setNextScrollContainerScrollRef.current
          )
          scrollContainerRef.current.scrollLeft = textAreaRef.current.scrollLeft
        }
        if (cursorContainerRef && cursorContainerRef.current && textAreaRef?.current) {
          cursorContainerRef.current.scrollLeft = textAreaRef.current.scrollLeft
        }
      }
      const textRef = textAreaRef.current
      return () => {
        if (textRef) {
          textRef.onscroll = null
        }
      }
    }
  }, [textAreaRef, parentRef, shouldUseCursor, cursorContainerRef])

  const reRenderOnLinesDataChange = useEffectEvent(() => {
    lineArrayMapRef.current = [...Array(Math.floor(linesData.length / numberOfLinesPerContentVisibility) + 1).keys()]
    reRender()
  })
  useEffect(() => {
    reRenderOnLinesDataChange()
  }, [linesData])

  return (
    <div
      ref={parentRef}
      style={{pointerEvents: shouldUseCursor ? 'none' : 'auto'}}
      onScroll={event => handleBlobScrollSync(event, textAreaRef)}
    >
      <div
        ref={scrollContainerRef}
        className={clsx(shouldUseCursor ? codeLinesStyles.scrollContainerHidden : codeLinesStyles.scrollContainerAuto)}
        //TODO: this is necessary to resolve an axe complaint, but we don't actually want this to be in the tab order.
        //unsure what the resolution to that issue is outside of an onFocus
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- pre-existing for axe compliance
        tabIndex={0}
        onScroll={() => {
          if (shouldUseCursor && scrollContainerRef.current && horizontalScrollBarRef.current) {
            if (!setNextScrollBarScrollRef.current) {
              setNextScrollBarScrollRef.current = true
              return
            }

            if (horizontalScrollBarRef.current.scrollLeft === scrollContainerRef.current.scrollLeft) return
            // Goes in a loop contianer -> scrollBar -> textArea -> container, so we need to prevent the next scroll on the container
            setNextScrollContainerScrollRef.current = !(
              setNextScrollBarScrollRef.current && setNextTextAreaScrollRef.current
            )
            horizontalScrollBarRef.current.scrollLeft = scrollContainerRef.current.scrollLeft
          }
        }}
      >
        <div
          className={clsx('react-code-file-contents', codeLinesStyles.codeFileContents)}
          role="presentation"
          aria-hidden
          data-tab-size={tabSize}
          data-paste-markdown-skip
          style={{tabSize, width: contentWidth, maxWidth: 'unset'}}
          data-hpc
        >
          <div
            className="react-line-numbers-no-virtualization"
            style={{
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 2,
            }}
          >
            {
              // eslint-disable-next-line react-hooks/refs
              lineArrayMapRef.current.map(segmentCount => {
                const tempLines = linesData.slice(
                  segmentCount * numberOfLinesPerContentVisibility,
                  Math.min(
                    segmentCount * numberOfLinesPerContentVisibility + numberOfLinesPerContentVisibility,
                    linesData.length,
                  ),
                )
                return (
                  <div
                    key={`line-number-wrapper-${segmentCount}-content:${tempLines[0]?.rawText?.substring(0, 100)}`}
                    className="react-no-virtualization-wrapper-lines"
                  >
                    {tempLines.map(lineData => {
                      return (
                        <LineNumberNoVirtualziation
                          codeLineData={lineData}
                          key={`line-number-${lineData.lineNumber}-content:${lineData.rawText?.substring(0, 100)}`}
                          onClick={onLineNumberClick}
                          ownedCodeSections={codeSections}
                          onLineStickOrUnstick={onLineStickOrUnstick}
                          onCollapseToggle={onCollapseToggle}
                          codeLineToSectionMap={codeLineToSectionMap}
                          copilotAccessAllowed={copilotAccessAllowed}
                        />
                      )
                    })}
                  </div>
                )
              })
            }
          </div>
          <div className="react-code-lines">
            <HighlightedSymbolsOverlay linesData={linesData} />
            <HighlightedLinesOverlay linesData={linesData} copilotAccessAllowed={copilotAccessAllowed} />
            <SyntaxHighlightedOverlay colorizedLines={colorizedLines} linesData={nonTruncatedLinesData!} />
            <StickyLineObserverOverlay
              linesData={linesData}
              sharedLineObserver={sharedLineObserver}
              onLineStickOrUnstick={onLineStickOrUnstick}
              codeLineToSectionMap={codeLineToSectionMap}
            />
            <CodeFoldingEllipsisOverlay
              linesData={linesData}
              onLineStickOrUnstick={onLineStickOrUnstick}
              setIsCollapsed={onCollapseToggle}
              tabSize={tabSize}
              contentWidth={contentWidth}
            />
          </div>
          <SelectAllShortcutButton
            shouldNotOverrideCopy={shouldUseCursor}
            containerRef={shouldUseCursor ? textAreaRef : parentRef}
          />
        </div>
      </div>
      {shouldUseCursor &&
      contentWidth &&
      // eslint-disable-next-line react-hooks/refs
      scrollContainerRef.current &&
      // eslint-disable-next-line react-hooks/refs
      scrollContainerRef.current.clientWidth < contentWidth ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- scrollbar container, pre-existing
        <div
          className={codeLinesStyles.scrollBarContainer}
          onScroll={() => {
            if (horizontalScrollBarRef.current && textAreaRef?.current) {
              if (!setNextTextAreaScrollRef.current) {
                setNextTextAreaScrollRef.current = true
                return
              }

              if (horizontalScrollBarRef.current.scrollLeft === textAreaRef.current.scrollLeft) return

              // Goes in a loop scrollBar -> textArea -> container -> scrollBar, so we need to prevent the next scroll on the scrollBar
              setNextScrollBarScrollRef.current = !(
                setNextScrollContainerScrollRef.current && setNextTextAreaScrollRef.current
              )
              textAreaRef.current.scrollLeft = horizontalScrollBarRef.current.scrollLeft
            }
          }}
          ref={horizontalScrollBarRef}
          // Prevent cursor events

          onClick={ev => ev.preventDefault()}
          onMouseDown={ev => ev.preventDefault()}
          onMouseUp={ev => ev.preventDefault()}
        >
          <div className={codeLinesStyles.scrollBarSpacer} style={{width: contentWidth}} />
        </div>
      ) : null}
    </div>
  )
}

export function useShouldDoNoVirtualization(fileLength: number) {
  const wordWrapEnabled = useCodeViewOptions().codeWrappingOption.enabled
  const isNonVirtualized = isFeatureEnabled('react_blob_overlay')
  const shouldUseInert = useShouldUseInert()
  const isFirefox = useIsFirefox()
  const isOldFirefox = isFirefox && !shouldUseInert

  //we want to virtualize beyond 3500 lines because that is when performance starts to significantly degrade
  return isNonVirtualized && !wordWrapEnabled && !isOldFirefox && fileLength < noVirtualizationThreshold
}
